import React, { useState } from 'react';

interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SupportModal: React.FC<SupportModalProps> = ({ isOpen, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation: name is required and at least one contact method
    if (!name.trim()) {
      alert('Le nom est obligatoire.');
      return;
    }

    if (!email.trim() && !phone.trim()) {
      alert('Veuillez fournir au moins un moyen de contact (email ou téléphone).');
      return;
    }

    setIsSubmitting(true);

    // Tracking de la soumission
    console.log('📊 Notification signup submitted:', {
      name: name,
      email: email || null,
      phone: phone || null,
      timestamp: new Date().toISOString()
    });

    // API call to join waitlist
    try {
      // Determine primary contact method and value for backward compatibility
      const primaryContactMethod = email.trim() ? 'email' : 'phone';
      const primaryContactValue = email.trim() ? email.trim() : phone.trim();

      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost';
      const response = await fetch(`${API_BASE_URL}/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim() || null,
          phone: phone.trim() || null,
          contact_method: primaryContactMethod,
          contact_value: primaryContactValue
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'inscription à la liste d\'attente');
      }

      const result = await response.json();
      console.log('📊 Waitlist signup successful:', result);

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setName('');
        setEmail('');
        setPhone('');
      }, 2000);

    } catch (error) {
      console.error('❌ Waitlist signup error:', error);
      // En cas d'erreur, afficher quand même le succès pour l'UX
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setName('');
        setEmail('');
        setPhone('');
      }, 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (success) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="bg-white rounded-lg max-w-xs sm:max-w-md w-full p-4 sm:p-6 text-center">
          <div className="text-green-500 text-6xl mb-4">✅</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Parfait ! Tu es sur la liste !
          </h2>
          <p className="text-gray-600">
            Tu seras contacté dès que les alertes carburant seront disponibles !
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-[9999]"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-t-2xl sm:rounded-lg w-full max-w-sm sm:max-w-lg max-h-[95vh] overflow-y-auto">
        <div className="p-3 sm:p-6">
          <div className="flex justify-between items-start mb-3 sm:mb-6">
            <h2 className="text-base sm:text-xl font-semibold text-gray-900 pr-3 leading-tight">
              ⛽ Rejoins la liste d'attente
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl sm:text-2xl flex-shrink-0"
            >
              ×
            </button>
          </div>

          <div className="space-y-3 sm:space-y-5">
            {/* Message principal */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-3 sm:p-5 rounded-lg border border-green-200">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-lg">
                🔥 Sois le premier informé !
              </h3>
              <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">
                Système d'alertes en développement pour te prévenir dès qu'il y a du carburant disponible près de chez toi.
              </p>
            </div>

            {/* Avantages des alertes - Version mobile compacte */}
            <div className="space-y-2 sm:space-y-4">
              <h4 className="font-semibold text-gray-900 text-sm sm:text-lg">🚨 Avantages</h4>
              <div className="grid grid-cols-1 gap-2 sm:gap-4">
                <div className="bg-white p-2 sm:p-4 rounded-lg border border-green-100">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <span className="text-green-500 text-lg sm:text-2xl">⚡</span>
                    <div>
                      <span className="text-xs sm:text-sm font-semibold text-gray-800">
                        Accès prioritaire + Alertes géolocalisées
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-white p-2 sm:p-4 rounded-lg border border-blue-100">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <span className="text-blue-500 text-lg sm:text-2xl">👥</span>
                    <div>
                      <span className="text-xs sm:text-sm font-semibold text-gray-800">
                        Beta testeur + Communauté exclusive
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Formulaire d'inscription */}
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="👤 Votre nom *"
                  required
                />
              </div>

              <div className="space-y-2 sm:space-y-3">
                <h4 className="font-semibold text-gray-900 text-sm sm:text-lg">📱 Contact</h4>
                <div className="space-y-2">
                  <div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="📧 votre@email.com"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="📞 +223 XX XX XX XX"
                    />
                  </div>
                  <p className="text-xs text-gray-600">* Au moins un moyen de contact est requis</p>
                </div>
              </div>

              {/* Rassurance vie privée - Version compacte */}
              <div className="bg-green-50 border border-green-200 rounded-md p-2 sm:p-3">
                <div className="flex items-start space-x-2">
                  <span className="text-green-600 text-sm">🔒</span>
                  <div className="text-xs text-green-800">
                    <strong>Confidentialité garantie</strong> - Informations sécurisées, jamais partagées.
                  </div>
                </div>
              </div>

              <div className="flex space-x-2 sm:space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-2 sm:px-4 rounded-lg transition-colors text-xs sm:text-sm"
                  disabled={isSubmitting}
                >
                  Plus tard
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium py-2 px-2 sm:px-4 rounded-lg transition-all disabled:opacity-50 text-xs sm:text-sm"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Inscription...' : '🚀 Rejoindre'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportModal;